import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import toast from 'react-hot-toast';
import {
  Save, RefreshCcw, Layers, CheckSquare, Square, Info, 
  ShieldAlert, Sparkles, Check, Play, Package, Trash2, Plus
} from 'lucide-react';
import { ALL_MODULES } from '../../utils/organizationModules';

const PRESET_KEYS = ['comercio', 'indumentaria', 'gastronomia', 'servicios'];

const DEFAULT_PRESETS = {
  comercio: {
    key: 'comercio',
    label: 'Comercio y Minimarket',
    shortLabel: 'Kiosco/Comercio',
    description: 'Ideal para kioscos, almacenes, despensas y dietéticas. Cobro rápido en mostrador (POS), control de stock simple y actualización de precios ágil.',
    cardTags: ['CAJA RÁPIDA', 'STOCK SIMPLE'],
    cardCta: 'Iniciar con caja rápida',
    storeType: 'retail',
    flags: {
      enable_variants: false,
      enable_cost_sync: false,
      show_pos_images: true,
      enable_misc_product: false,
      allow_negative_stock: false,
      allow_zero_sell_price: false
    },
    categories: ['GENERAL', 'MOSTRADOR', 'BEBIDAS', 'ALIMENTOS'],
    units: [
      { name: 'UNIDAD', abbreviation: 'un' }
    ],
    modules: [
      'dashboard', 'ventas-pos', 'ventas-historial', 'caja', 'clientes', 
      'productos', 'inventario', 'inventario-ajuste', 'gastos', 'rentabilidad-ventas',
      'empresa-datos', 'sitios', 'categorias', 'unidades-medida', 'metodos-cobro', 'importaciones'
    ]
  },
  indumentaria: {
    key: 'indumentaria',
    label: 'Indumentaria y Calzado',
    shortLabel: 'Indumentaria',
    description: 'Optimizado para tiendas de ropa, calzado y accesorios. Permite controlar de manera simple tus ventas por variantes de talle, color y marcas.',
    cardTags: ['TALLES Y COLORES', 'MARCAS'],
    cardCta: 'Iniciar con catálogo de ropa',
    storeType: 'retail',
    flags: {
      enable_variants: true,
      enable_cost_sync: false,
      show_pos_images: true,
      enable_misc_product: false,
      allow_negative_stock: false,
      allow_zero_sell_price: false
    },
    categories: ['REMERAS', 'PANTALONES', 'ABRIGOS', 'CALZADO'],
    units: [
      { name: 'UNIDAD', abbreviation: 'un' }
    ],
    modules: [
      'dashboard', 'ventas-pos', 'ventas-historial', 'caja', 'clientes', 
      'productos', 'inventario', 'inventario-ajuste', 'listas-precios', 'rentabilidad-ventas',
      'empresa-datos', 'sitios', 'categorias', 'unidades-medida', 'metodos-cobro', 'importaciones'
    ]
  },
  gastronomia: {
    key: 'gastronomia',
    label: 'Gastronomía y Viandas',
    shortLabel: 'Gastronomía',
    description: 'Pensado para rotiserías, restaurantes, panaderías y viandas. Agiliza la toma de pedidos, recetas de insumos y producción interna.',
    cardTags: ['TOMA DE PEDIDOS', 'RECETAS Y PRODUCCIÓN'],
    cardCta: 'Iniciar gestión de pedidos',
    storeType: 'gastronomy',
    flags: {
      enable_variants: false,
      enable_cost_sync: true,
      show_pos_images: true,
      enable_misc_product: true,
      allow_negative_stock: true,
      allow_zero_sell_price: false
    },
    categories: ['PLATOS', 'BEBIDAS', 'POSTRES', 'INSUMOS'],
    units: [
      { name: 'UNIDAD', abbreviation: 'un' },
      { name: 'KILOGRAMO', abbreviation: 'kg' },
      { name: 'LITRO', abbreviation: 'lt' }
    ],
    modules: [
      'dashboard', 'ventas-pos', 'ventas-historial', 'caja', 'clientes', 
      'productos', 'inventario', 'inventario-ajuste', 'gastos', 'rentabilidad-ventas',
      'recetas', 'produccion-registrar', 'produccion-historial', 'produccion-distribucion',
      'empresa-datos', 'sitios', 'categorias', 'unidades-medida', 'metodos-cobro', 'importaciones'
    ]
  },
  servicios: {
    key: 'servicios',
    label: 'Servicios y Consultoría',
    shortLabel: 'Servicios',
    description: 'Ideal para talleres, centros de estética, profesionales e intangibles. Arma presupuestos al instante, factura servicios y gestiona abonos.',
    cardTags: ['PRESUPUESTOS', 'PRODUCTOS LIBRES'],
    cardCta: 'Iniciar gestión de servicios',
    storeType: 'retail',
    flags: {
      enable_variants: false,
      enable_cost_sync: false,
      show_pos_images: false,
      enable_misc_product: true,
      allow_negative_stock: true,
      allow_zero_sell_price: false
    },
    categories: ['SERVICIOS', 'ABONOS', 'ASESORAMIENTO'],
    units: [
      { name: 'UNIDAD', abbreviation: 'un' },
      { name: 'HORA', abbreviation: 'hs' }
    ],
    modules: [
      'dashboard', 'ventas-pos', 'ventas-historial', 'caja', 'clientes', 'presupuestos',
      'productos', 'inventario', 'gastos', 'rentabilidad-ventas',
      'empresa-datos', 'sitios', 'categorias', 'unidades-medida', 'metodos-cobro', 'presupuestos-config', 'importaciones'
    ]
  }
};

const groupModulesByCategory = () => {
  return ALL_MODULES.reduce((acc, m) => {
    // Excluir módulos específicos de SuperAdmin de las opciones del rubro
    if (m.id.startsWith('super-admin-') || m.id === 'logs') return acc;
    const cat = m.category || 'Otros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(m);
    return acc;
  }, {});
};

export default function SuperAdminPresets() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePresetTab, setActivePresetTab] = useState('comercio');
  const [presets, setPresets] = useState(DEFAULT_PRESETS);

  // Campos de categoría semilla para la UI
  const [newCategoryName, setNewCategoryName] = useState('');

  const modulesByCategory = useMemo(groupModulesByCategory, []);

  const fetchPresets = async () => {
    setLoading(true);
    try {
      const { data: settings, error } = await supabase
        .from('app_settings')
        .select('business_presets')
        .eq('id', 'global')
        .maybeSingle();

      if (error) throw error;

      if (settings?.business_presets && typeof settings.business_presets === 'object' && Object.keys(settings.business_presets).length > 0) {
        let merged = { ...DEFAULT_PRESETS };
        for (const k of PRESET_KEYS) {
          if (settings.business_presets[k]) {
            merged[k] = {
              ...DEFAULT_PRESETS[k],
              ...settings.business_presets[k]
            };
          }
        }
        setPresets(merged);
      } else {
        setPresets(DEFAULT_PRESETS);
      }
    } catch (err) {
      console.error('[SuperAdminPresets] Error loading:', err);
      toast.error('Error al cargar la configuración de rubros');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPresets();
  }, []);

  const activePreset = presets[activePresetTab] || DEFAULT_PRESETS[activePresetTab];

  const handleUpdateField = (field, value) => {
    setPresets(prev => ({
      ...prev,
      [activePresetTab]: {
        ...prev[activePresetTab],
        [field]: value
      }
    }));
  };

  const handleUpdateFlag = (flagKey, value) => {
    setPresets(prev => ({
      ...prev,
      [activePresetTab]: {
        ...prev[activePresetTab],
        flags: {
          ...(prev[activePresetTab]?.flags || {}),
          [flagKey]: value
        }
      }
    }));
  };

  const handleToggleModule = (moduleId) => {
    setPresets(prev => {
      const currentModules = prev[activePresetTab]?.modules || [];
      const updated = currentModules.includes(moduleId)
        ? currentModules.filter(id => id !== moduleId)
        : [...currentModules, moduleId];
      return {
        ...prev,
        [activePresetTab]: {
          ...prev[activePresetTab],
          modules: updated
        }
      };
    });
  };

  const handleToggleCategoryModules = (mods, allSelected) => {
    setPresets(prev => {
      const currentModules = new Set(prev[activePresetTab]?.modules || []);
      mods.forEach(m => {
        if (allSelected) currentModules.delete(m.id);
        else currentModules.add(m.id);
      });
      return {
        ...prev,
        [activePresetTab]: {
          ...prev[activePresetTab],
          modules: Array.from(currentModules)
        }
      };
    });
  };

  const handleAddCategorySeed = (e) => {
    e.preventDefault();
    const name = newCategoryName.trim().toUpperCase();
    if (!name) return;
    const current = activePreset.categories || [];
    if (current.includes(name)) {
      toast.error('La categoría ya existe en la lista');
      return;
    }
    handleUpdateField('categories', [...current, name]);
    setNewCategoryName('');
  };

  const handleRemoveCategorySeed = (name) => {
    const current = activePreset.categories || [];
    handleUpdateField('categories', current.filter(c => c !== name));
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({
          business_presets: presets,
          updated_at: new Date().toISOString()
        })
        .eq('id', 'global');

      if (error) throw error;

      toast.success('Módulos y reglas de rubros actualizados correctamente');
      
      // Emitir evento local para actualizar caché en otras pestañas
      try {
        window.localStorage.setItem('gastify:presets-updated', String(Date.now()));
      } catch {}
    } catch (err) {
      console.error('[SuperAdminPresets] Error saving:', err);
      toast.error(err?.message || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#E4E4E7] border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
      
      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#10B981]">Panel de SuperAdmin</div>
          <h1 className="text-2xl font-black text-[#3F3F46] mt-1">Configuración de Rubros (Presets)</h1>
          <p className="text-[13px] text-[#A1A1AA] mt-1">
            Asigna qué módulos de cocina (producción), comercio (kiosco), indumentaria o servicios van a cada tipo de negocio al registrarse.
          </p>
        </div>
        <button
          onClick={fetchPresets}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold text-[12px] hover:bg-emerald-500 hover:text-white transition-all shadow-sm"
        >
          <RefreshCcw size={14} /> Recargar
        </button>
      </div>

      {/* Selector de Rubros (Tabs) */}
      <div className="flex gap-2 border-b border-[#E4E4E7] mb-6 overflow-x-auto no-scrollbar pb-1">
        {PRESET_KEYS.map((key) => {
          const p = presets[key];
          const active = activePresetTab === key;
          return (
            <button
              key={key}
              onClick={() => {
                setActivePresetTab(key);
                setNewCategoryName('');
              }}
              className={`px-5 py-3 text-[12px] font-black uppercase tracking-wider transition-all border-b-2 whitespace-nowrap shrink-0 ${
                active 
                  ? 'border-[#10B981] text-emerald-700' 
                  : 'border-transparent text-[#A1A1AA] hover:text-[#3F3F46]'
              }`}
            >
              {p?.shortLabel || key}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        
        {/* Columna Izquierda: Identidad y Flags del Rubro */}
        <div className="lg:col-span-1 space-y-6">
          
          {/* Identidad */}
          <div className="bg-[var(--surface)] rounded-md border border-[#E4E4E7] p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-black text-[#3F3F46] flex items-center gap-2 border-b pb-2">
              <Sparkles size={16} className="text-[#10B981]" /> Identidad del Rubro
            </h3>
            
            <div>
              <label className="block text-[10px] font-black text-[#71717A] uppercase tracking-wider mb-1.5">
                Nombre de Rubro
              </label>
              <input
                type="text"
                value={activePreset.label}
                onChange={(e) => handleUpdateField('label', e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-[#E4E4E7] text-[13px] font-semibold text-[#3F3F46] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition-all"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-[#71717A] uppercase tracking-wider mb-1.5">
                Etiqueta Corta
              </label>
              <input
                type="text"
                value={activePreset.shortLabel}
                onChange={(e) => handleUpdateField('shortLabel', e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-[#E4E4E7] text-[13px] font-semibold text-[#3F3F46] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition-all"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-[#71717A] uppercase tracking-wider mb-1.5">
                Descripción
              </label>
              <textarea
                value={activePreset.description}
                onChange={(e) => handleUpdateField('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-md border border-[#E4E4E7] text-[12px] font-medium text-[#71717A] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition-all resize-none"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-[#71717A] uppercase tracking-wider mb-1.5">
                Modo de Tienda Virtual
              </label>
              <select
                value={activePreset.storeType || 'retail'}
                onChange={(e) => handleUpdateField('storeType', e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-[#E4E4E7] text-[13px] font-semibold text-[#3F3F46] outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition-all"
              >
                <option value="retail">Comercio Minorista (Retail)</option>
                <option value="gastronomy">Gastronomía (Pedidos Online)</option>
              </select>
            </div>
          </div>

          {/* Reglas Operativas (Flags) */}
          <div className="bg-[var(--surface)] rounded-md border border-[#E4E4E7] p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-black text-[#3F3F46] flex items-center gap-2 border-b pb-2">
              <ShieldAlert size={16} className="text-[#10B981]" /> Reglas Operativas
            </h3>

            <div className="space-y-3">
              {[
                { key: 'enable_variants', label: 'Talles, Colores y Variantes', desc: 'Permite crear variantes complejas.' },
                { key: 'enable_cost_sync', label: 'Sincronizar costo en recetas', desc: 'Recalcula el costo del plato según insumos.' },
                { key: 'show_pos_images', label: 'Mostrar imágenes en POS', desc: 'Renderiza catálogo visual en la caja.' },
                { key: 'enable_misc_product', label: 'Habilitar producto libre', desc: 'Cobros rápidos sin cargar ficha técnica.' },
                { key: 'allow_negative_stock', label: 'Permitir stock negativo', desc: 'Venta flexible sin bloqueo físico.' },
                { key: 'allow_zero_sell_price', label: 'Permitir precio sugerido $0', desc: 'Útil para productos bajo cotización.' }
              ].map((flag) => {
                const active = Boolean(activePreset.flags?.[flag.key]);
                return (
                  <button
                    key={flag.key}
                    type="button"
                    onClick={() => handleUpdateFlag(flag.key, !active)}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active 
                        ? 'border-emerald-300 bg-emerald-50/30' 
                        : 'border-[#E4E4E7] hover:border-emerald-200 bg-[var(--surface)]'
                    }`}
                  >
                    <div className={`mt-0.5 shrink-0 ${active ? 'text-[#10B981]' : 'text-slate-300'}`}>
                      {active ? <CheckSquare size={16} /> : <Square size={16} />}
                    </div>
                    <div>
                      <div className="text-[12px] font-black text-[#3F3F46] leading-none">{flag.label}</div>
                      <div className="text-[10px] text-[#A1A1AA] mt-1">{flag.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Columna Derecha: Módulos y Semillas */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Módulos Habilitados */}
          <div className="bg-[var(--surface)] rounded-md border border-[#E4E4E7] p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-black text-[#3F3F46] flex items-center gap-2 border-b pb-2">
              <Package size={16} className="text-[#10B981]" /> Módulos asignados al registrarse
            </h3>
            
            <p className="text-[11px] text-[#71717A] bg-cream-50 px-3 py-2 rounded border leading-relaxed">
              Los clientes que elijan el rubro <strong>{activePreset.label}</strong> recibirán estos módulos activos por defecto en su espacio de trabajo, independientemente del plan (se suma a las restricciones del plan).
            </p>

            <div className="space-y-4 max-h-[460px] overflow-y-auto pr-2 border rounded-md p-4 bg-[var(--surface)]">
              {Object.entries(modulesByCategory).map(([category, mods]) => {
                const activeMods = activePreset.modules || [];
                const allSelected = mods.every((m) => activeMods.includes(m.id));
                const someSelected = mods.some((m) => activeMods.includes(m.id));
                return (
                  <div key={category} className="border-b last:border-0 pb-3 mb-3">
                    <button
                      onClick={() => handleToggleCategoryModules(mods, allSelected)}
                      className="flex items-center gap-2 mb-2.5 text-[11px] font-black uppercase tracking-widest text-[#3F3F46] hover:text-emerald-700 transition-colors"
                    >
                      {allSelected ? (
                        <CheckSquare size={14} className="text-[#10B981]" />
                      ) : someSelected ? (
                        <CheckSquare size={14} className="text-[#10B981] opacity-50" />
                      ) : (
                        <Square size={14} className="text-[#A1A1AA]" />
                      )}
                      {category}
                      <span className="text-[10px] font-bold text-[#A1A1AA]">
                        ({mods.filter((m) => activeMods.includes(m.id)).length}/{mods.length})
                      </span>
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5 ml-5">
                      {mods.map((m) => {
                        const checked = activeMods.includes(m.id);
                        return (
                          <label
                            key={m.id}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-md cursor-pointer text-[12px] font-semibold border transition-all ${
                              checked
                                ? 'bg-emerald-50/50 text-emerald-800 border-emerald-200'
                                : 'bg-[var(--surface)] text-[#52525B] border-[#E4E4E7] hover:border-emerald-400'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleToggleModule(m.id)}
                              className="hidden"
                            />
                            {checked ? (
                              <CheckSquare size={12} className="text-[#10B981] shrink-0" />
                            ) : (
                              <Square size={12} className="text-[#A1A1AA] shrink-0" />
                            )}
                            <span className="truncate">{m.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Semillas de Categorías de Producto */}
          <div className="bg-[var(--surface)] rounded-md border border-[#E4E4E7] p-5 shadow-sm space-y-4">
            <h3 className="text-sm font-black text-[#3F3F46] flex items-center gap-2 border-b pb-2">
              <Layers size={16} className="text-[#10B981]" /> Categorías Iniciales (Semillas)
            </h3>
            
            <p className="text-[11px] text-[#71717A]">
              Estas categorías de catálogo se crearán automáticamente al configurar el rubro, evitando que la tienda arranque vacía.
            </p>

            <form onSubmit={handleAddCategorySeed} className="flex gap-2">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="NUEVA CATEGORÍA (ej: BEBIDAS)"
                className="flex-1 px-3 py-2 rounded-md border border-[#E4E4E7] text-[12px] font-bold text-[#3F3F46] outline-none focus:border-emerald-500 uppercase placeholder:text-slate-300"
              />
              <button
                type="submit"
                className="px-4 rounded-md bg-emerald-500 text-white font-black text-[11px] uppercase tracking-wider hover:bg-emerald-600 transition-all flex items-center gap-1.5"
              >
                <Plus size={14} /> Añadir
              </button>
            </form>

            <div className="flex flex-wrap gap-2 pt-2">
              {(activePreset.categories || []).map((catName) => (
                <span
                  key={catName}
                  className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-100 rounded-md px-3 py-1.5 text-[11px] font-bold uppercase"
                >
                  {catName}
                  <button
                    type="button"
                    onClick={() => handleRemoveCategorySeed(catName)}
                    className="text-[#EF4444] hover:text-red-700"
                  >
                    <Trash2 size={12} />
                  </button>
                </span>
              ))}
              {(activePreset.categories || []).length === 0 && (
                <span className="text-xs font-semibold text-[#A1A1AA]">No hay categorías de semilla configuradas.</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Guardar botón fijo al fondo */}
      <div className="mb-10">
        <button
          onClick={handleSaveConfig}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 py-4 px-6 rounded-md bg-emerald-500 text-white font-black text-[12px] uppercase tracking-widest hover:bg-emerald-600 transition-all disabled:opacity-50 shadow-lg shadow-emerald-500/20"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          ) : (
            <Save size={15} />
          )}
          {saving ? 'Guardando cambios...' : `Guardar configuración de rubro: ${activePreset.label}`}
        </button>
      </div>
    </div>
  );
}
